import { api } from '../console/api.ts';
import { empty, pageHead, pill } from '../console/components.ts';
import { agentName, date, esc, fmt, icon, type Money, person } from '../console/format.ts';
import { CHANNEL, OUTCOME, STANDING } from './cobranza.ts';

/**
 * «Cartera vencida» (#315): the receipts in arrears, by age (1-30, 31-60, 61-90 and more than 90
 * days), most overdue first, with their latest collection effort and how a promise to pay
 * stands. Each opens its policy's «Prima y recibos», where collections records the next effort.
 * The collections officer's day; management and the manager follow it.
 */

type ArrearsItem = {
  scheduleId: string;
  carrier: string;
  policyNumber: string;
  agentId: string;
  holder: string | null;
  installment: number;
  installments: number;
  dueDate: string;
  amount: Money;
  daysPastDue: number;
  bucket: string;
  lastEffort: {
    on: string;
    channel: string;
    outcome: string;
    by: string;
    promisedOn: string | null;
    standing: string | null;
  } | null;
};
type Arrears = {
  today: string;
  totals: { bucket: string; amount: Money; receipts: number }[];
  items: ArrearsItem[];
};

/** The ages, oldest last, as collections names them. */
export const BUCKETS: [key: string, label: string, tone: string][] = [
  ['D1_30', '1 a 30 días', 'warn'],
  ['D31_60', '31 a 60 días', 'warn'],
  ['D61_90', '61 a 90 días', 'bad'],
  ['D90_PLUS', 'Más de 90 días', 'bad'],
];
const BUCKET_LABEL = Object.fromEntries(BUCKETS.map(([k, l]) => [k, l]));
const BUCKET_TONE = Object.fromEntries(BUCKETS.map(([k, , t]) => [k, t]));

/** The latest effort of a receipt, in words (and a promise's standing, never by color alone). */
function effortCell(e: ArrearsItem['lastEffort']) {
  if (!e) return '<span class="muted">Sin gestión</span>';
  const standing = e.standing ? STANDING[e.standing] : null;
  return `${esc(OUTCOME[e.outcome] ?? e.outcome)}${e.promisedOn ? ` · pagará el ${esc(date(e.promisedOn))}` : ''}${standing ? ` ${pill(standing[1], standing[0])}` : ''}
    <div class="muted small">${esc(CHANNEL[e.channel] ?? e.channel)}, ${esc(date(e.on))} · ${esc(person(e.by))}</div>`;
}

export function carteraHtml(a: Arrears) {
  const head = pageHead(
    'Cartera vencida',
    'Recibos vencidos por antigüedad, del más atrasado al más reciente, con su última gestión de cobro y las promesas de pago.',
  );
  const currencies = [...new Set(a.totals.map((t) => t.amount.currency))].sort();
  const kpis = currencies
    .map(
      (cur) =>
        `<section class="kpis" data-component="arrears-kpis" aria-label="${esc(`Cartera vencida en ${cur === 'USD' ? 'dólares' : 'quetzales'}`)}">${BUCKETS.map(
          ([key, label, tone]) => {
            const t = a.totals.find((x) => x.bucket === key && x.amount.currency === cur);
            return `<div class="card kpi ${t ? tone : ''}" data-bucket="${esc(key)}"><span class="kpi-label">${icon('clock')}${esc(label)}</span><span class="kpi-value">${fmt(t?.amount ?? { amountMinor: 0, currency: cur })}</span><span class="kpi-foot">${t?.receipts ?? 0} ${t?.receipts === 1 ? 'recibo' : 'recibos'}</span></div>`;
          },
        ).join('')}</section>`,
    )
    .join('');
  if (!a.items.length)
    return `${head}${empty('ok', 'No hay recibos vencidos', 'Todos los recibos de la cartera están al día o por vencer.', '', 2)}`;
  const rows = a.items
    .map(
      (
        i,
      ) => `<tr data-arrears-row><td><span class="mono">${esc(i.policyNumber)}</span><div class="muted small">${esc(i.holder ?? 'Contratante sin registrar')} · ${esc(agentName(i.agentId))}</div></td>
        <td class="mono">${i.installment}/${i.installments}</td><td class="nowrap">${esc(date(i.dueDate))}</td>
        <td class="num">${fmt(i.amount)}</td>
        <td>${pill(BUCKET_TONE[i.bucket] ?? 'warn', `${i.daysPastDue} ${i.daysPastDue === 1 ? 'día' : 'días'}`)}<div class="muted small">${esc(BUCKET_LABEL[i.bucket] ?? i.bucket)}</div></td>
        <td>${effortCell(i.lastEffort)}</td>
        <td class="actions"><button type="button" class="btn sm" data-policy-open="${esc(i.scheduleId)}" data-tab="prima" aria-label="${esc(`Gestionar el recibo ${i.installment} de la póliza ${i.policyNumber}`)}">Gestionar</button></td></tr>`,
    )
    .join('');
  return `${head}${kpis}<section class="card"><div class="card-head"><h2>Recibos vencidos</h2><span class="spacer"></span><span class="muted small">${a.items.length} ${a.items.length === 1 ? 'recibo' : 'recibos'} al ${esc(date(a.today))}</span></div>
    <div class="table-wrap" data-component="table"><table class="table arrears"><thead><tr><th>Póliza</th><th>Recibo</th><th>Venció</th><th class="num">Monto</th><th>Atraso</th><th>Última gestión</th><th class="actions"><span class="sr-only">Acción</span></th></tr></thead><tbody>${rows}</tbody></table></div></section>`;
}

export async function cartera() {
  return carteraHtml(await api<Arrears>('GET', '/v1/arrears'));
}
